import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Paperclip, ArrowUp, Loader2, X, FileText, Image as ImageIcon, Mic, MicOff, Square } from 'lucide-react';
import { API, uploadHeaders } from './chatUtils';

export default function ChatInput({
  input, setInput, isLoading, pendingFile, setPendingFile,
  onSend, onFileSelect, inputRef: externalRef,
}) {
  const localRef = useRef(null);
  const inputRef = externalRef || localRef;
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) await onFileSelect(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  };

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 100) return;
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, 'recording.webm');
          const res = await fetch(`${API}/api/voice/stt`, {
            method: 'POST', headers: uploadHeaders(), body: formData,
          });
          if (res.ok) {
            const data = await res.json();
            if (data.text) {
              setInput(prev => prev ? `${prev} ${data.text}` : data.text);
              inputRef.current?.focus();
            }
          }
        } catch (e) {
          console.error('STT error:', e);
        } finally {
          setIsTranscribing(false);
        }
      };
      mediaRecorder.start();
      setIsRecording(true);
    } catch {
      alert('Microphone access denied.');
    }
  }, [isRecording, setInput, inputRef]);

  const hasContent = input.trim() || pendingFile;

  return (
    <div className="px-4 md:px-6 pb-4 pt-2 flex-shrink-0" data-testid="chat-input-area">
      <div className="max-w-3xl mx-auto">
        {/* Pending file */}
        {pendingFile && (
          <div className="mb-2">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] bg-overlay text-content-2 border border-line">
              {pendingFile.file_type?.startsWith('image/') ? <ImageIcon className="w-3.5 h-3.5 text-emerald-500" strokeWidth={1.5} /> : <FileText className="w-3.5 h-3.5 text-emerald-500" strokeWidth={1.5} />}
              <span className="truncate max-w-[200px] font-sora text-[12px]">{pendingFile.file_name}</span>
              <button onClick={() => setPendingFile(null)} className="p-0.5 rounded text-content-3 hover:text-red-400 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="relative flex items-end bg-surface border border-line rounded-2xl px-2 py-1.5 focus-within:border-line-2 transition-all duration-200">
          <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden"
            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/csv,.md,.json" data-testid="file-input" />

          <button onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-xl text-content-3 hover:text-content-2 hover:bg-overlay-hover transition-colors active:scale-95 flex-shrink-0"
            data-testid="attach-file-btn">
            <Paperclip className="w-5 h-5" strokeWidth={1.5} />
          </button>

          <button onClick={toggleRecording} disabled={isTranscribing}
            className={`p-2 rounded-xl transition-all active:scale-95 flex-shrink-0
              ${isRecording ? 'bg-red-500/15 text-red-400' : isTranscribing ? 'text-emerald-500' : 'text-content-3 hover:text-content-2 hover:bg-overlay-hover'}`}
            data-testid="voice-input-btn" title={isRecording ? 'Stop recording' : 'Voice input'}>
            {isTranscribing ? <Loader2 className="w-5 h-5 animate-spin" /> : isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" strokeWidth={1.5} />}
          </button>

          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); autoResize(e.target); }}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? 'Listening...' : 'Message OpenSov AI...'}
            rows={1}
            className="flex-1 bg-transparent text-content text-[14px] placeholder-content-3 resize-none outline-none max-h-[200px] leading-relaxed py-2.5 px-1 font-sora"
            style={{ height: 'auto', minHeight: '24px' }}
            data-testid="chat-input"
          />

          <div className="flex-shrink-0 pb-0.5">
            {isLoading ? (
              <button className="p-2 rounded-xl bg-overlay-strong text-content-2 transition-colors" data-testid="stop-btn">
                <Square className="w-4 h-4" fill="currentColor" />
              </button>
            ) : (
              <button onClick={onSend} disabled={!hasContent}
                className={`p-2 rounded-xl transition-all duration-150 active:scale-90
                  ${hasContent ? 'bg-btn text-btn-foreground hover:opacity-90' : 'bg-overlay text-content-3'}`}
                data-testid="send-btn">
                <ArrowUp className="w-5 h-5" strokeWidth={2} />
              </button>
            )}
          </div>
        </div>

        <p className="text-[11px] text-content-3 text-center mt-2 font-sora">
          OpenSov AI can make mistakes. Verify important information.
        </p>
      </div>
    </div>
  );
}
